/**
 * crossTabBroadcast — Phase 2B of the Tracker caching plan.
 *
 * Wires same-origin browser tabs together so a mutation in Tab A causes
 * the TanStack Query caches in Tab B (and C, D, ...) to be marked
 * invalidated immediately. Without this, Phase 1's localStorage
 * persister makes the cross-tab incoherence WORSE: Tab B keeps showing
 * the stale (now persisted) value until the user refocuses or the
 * 30-min staleTime elapses.
 *
 * Mechanism: a single same-origin ``BroadcastChannel`` named
 * ``cc-tracker-cache``. The producer (mutation ``onSettled``) calls
 * ``publishInvalidate(keys)``; the consumer (an effect at app boot)
 * subscribes once per tab via ``subscribeInvalidate(queryClient)`` and
 * forwards every received key list to
 * ``queryClient.invalidateQueries({ queryKey })``.
 *
 * Self-echo guard — the ``senderId`` nonce
 * ---------------------------------------
 * The browser's BroadcastChannel API delivers messages to ALL listeners
 * on the channel — INCLUDING the listener in the SAME tab that
 * published. Without a guard, every mutation in Tab A would:
 *   1. Invalidate locally (the ``onSettled`` direct ``invalidateQueries``)
 *   2. Publish to the channel
 *   3. Receive its own broadcast
 *   4. Invalidate AGAIN
 * Step 4 isn't catastrophic (TanStack dedups identical invalidates
 * within the same microtask) but it's wasteful and noisy in devtools.
 * Solution: stamp every outbound message with a per-tab random nonce
 * (``senderId``) generated once at module load, and the receiver drops
 * any message whose ``senderId`` matches the local one.
 *
 * Availability — best-effort, never throws
 * ----------------------------------------
 * ``BroadcastChannel`` is available in every browser the Tracker
 * supports as of 2026 (Chrome, FF, Edge, Safari 15.4+). On older Safari
 * or in test environments (jsdom doesn't ship it pre-v22), the
 * functions degrade to no-ops — Phase 1's localStorage persister still
 * gives cross-tab coherence on next focus/reload, just not in-flight.
 *
 * SSR safety: every function bails early if ``typeof window`` or
 * ``BroadcastChannel`` is undefined.
 */
import type { QueryClient } from "@tanstack/react-query";

const CHANNEL_NAME = "cc-tracker-cache";

type InvalidateMessage = {
  kind: "invalidate";
  keys: ReadonlyArray<ReadonlyArray<unknown>>;
  ts: number;
  senderId: string;
};

const isBroadcastChannelAvailable = (): boolean =>
  typeof window !== "undefined" && typeof window.BroadcastChannel === "function";

const generateSenderId = (): string => {
  // ``crypto.randomUUID`` is everywhere BroadcastChannel is. Fallback
  // (Math.random) is just for jsdom-style test envs where this code
  // never runs the receive path anyway.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `cc-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
};

const senderId = generateSenderId();

let cachedChannel: BroadcastChannel | null = null;

const getChannel = (): BroadcastChannel | null => {
  if (!isBroadcastChannelAvailable()) return null;
  if (cachedChannel) return cachedChannel;
  try {
    cachedChannel = new window.BroadcastChannel(CHANNEL_NAME);
    return cachedChannel;
  } catch {
    // ``new BroadcastChannel`` should not throw in practice, but if a
    // browser extension or sandbox blocks it, fail closed.
    return null;
  }
};

/**
 * Broadcast a cache-invalidation intent to every other same-origin
 * tab. The local tab does NOT receive its own message (guarded by
 * ``senderId`` on the subscriber side).
 *
 * Best-effort: silently no-ops if BroadcastChannel is unavailable.
 * Never throws — callers should not need a try/catch.
 *
 * @param keys an array of TanStack query keys (each key is itself an
 *   array, matching ``queryClient.invalidateQueries({ queryKey })``).
 */
export const publishInvalidate = (
  keys: ReadonlyArray<ReadonlyArray<unknown>>,
): void => {
  if (keys.length === 0) return;
  const channel = getChannel();
  if (!channel) return;
  try {
    const message: InvalidateMessage = {
      kind: "invalidate",
      keys,
      ts: Date.now(),
      senderId,
    };
    channel.postMessage(message);
  } catch {
    // Best-effort. A serialization failure (keys containing functions /
    // class instances) should never happen with our query-key shapes,
    // but if it does we'd rather silently miss the cross-tab refresh
    // than crash the mutation chain.
  }
};

const isInvalidateMessage = (data: unknown): data is InvalidateMessage => {
  if (typeof data !== "object" || data === null) return false;
  const m = data as Record<string, unknown>;
  return (
    m.kind === "invalidate" &&
    Array.isArray(m.keys) &&
    typeof m.senderId === "string"
  );
};

/**
 * Subscribe this tab's QueryClient to invalidation broadcasts from
 * sibling tabs. Call ONCE at app boot. Returns an unsubscribe function
 * (closing the BroadcastChannel) — in practice the app's lifetime IS
 * the channel's lifetime, but the contract is useful for tests.
 *
 * Skips self-echoes via the ``senderId`` nonce.
 */
export const subscribeInvalidate = (queryClient: QueryClient): (() => void) => {
  const channel = getChannel();
  if (!channel) return () => {};

  const handler = (event: MessageEvent<unknown>) => {
    const data = event.data;
    if (!isInvalidateMessage(data)) return;
    // Self-echo guard — drop messages we sent ourselves.
    if (data.senderId === senderId) return;
    for (const queryKey of data.keys) {
      void queryClient.invalidateQueries({ queryKey: [...queryKey] });
    }
  };

  channel.addEventListener("message", handler);

  return () => {
    channel.removeEventListener("message", handler);
    try {
      channel.close();
    } catch {
      // No-op; channel may already be closed.
    }
    if (cachedChannel === channel) {
      cachedChannel = null;
    }
  };
};

/**
 * Test-only escape hatch: returns the per-tab senderId so unit tests
 * can stub the self-echo path. NOT used in production code.
 */
export const __testOnly_getSenderId = (): string => senderId;
